import React, {useContext, useEffect, useRef, useState} from 'react'
import styled from 'styled-components';
import {
    Accordion,
    AccordionContext,
    Badge,
    Button,
    Card,
    Col,
    OverlayTrigger,
    Row,
    Tooltip,
    useAccordionButton
} from 'react-bootstrap';
import {getDailyTicker, MARKETS} from '../../actions/caremgmt/hubWorkCenterAction';
import {DropDownList} from '../../components/dropDownLists/genericDDL';

const DropDownDiv = styled.div`
#GenericDDL--filterValue { margin-bottom: 0!important; }
#GenericDDL--filter { margin-bottom: 0!important; }
`;

const componentName = "PatientListOffCanvas";

function PlusMinusToggle({ children, eventKey, callback, idx }) {
  const { activeEventKey } = useContext(AccordionContext);
  const decoratedOnClick = useAccordionButton(
    eventKey,
    () => callback && callback(eventKey),
  );

  const isCurrentEventKey = activeEventKey === eventKey;

  return (
    <Button variant="link" className="d-flex align-items-center text-dark text-decoration-none w-100 p-0" onClick={decoratedOnClick}>
      <i id={`${componentName}-plusMinusIcon-${idx}`} className={isCurrentEventKey ? "far fa-minus-circle me-2" : "far fa-plus-circle me-2"}></i>{" "}
      <div id={`${componentName}-itemTitle-${idx}`} className="d-flex justify-content-between align-items-center w-100">{children}</div>
    </Button>
  );
}

function PatientListOffCanvas({
  onError,
  teams,
  setTeams,
  top
}) {

  const [count, setCount] = useState(0);
  const [selectedMarkets, setSelectedMarket] = useState({});
  const [showOverlay, setShowOverlay] = useState(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchDailyTicker();
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        setCount(currentCount => currentCount + 1);
      }
    }, 60000);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, selectedMarkets]);

  useEffect(() => {
    const offcanvasElement = document.getElementById('offcanvasTeamList');
    const handleHidden = () => handleOffCanvas(false);
    const handleShown = () => handleOffCanvas(true);
    offcanvasElement.addEventListener('hidden.bs.offcanvas', handleHidden);
    offcanvasElement.addEventListener('shown.bs.offcanvas', handleShown);
    return () => {
      offcanvasElement.removeEventListener('hidden.bs.offcanvas', handleHidden);
      offcanvasElement.removeEventListener('shown.bs.offcanvas', handleShown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOffCanvas = (state) => {
    state && fetchDailyTicker()
    if (isMountedRef.current) {
      setShowOverlay(state)
    }
  }

  const fetchDailyTicker = () => {
    const market = selectedMarkets?.displayValue?.join(",");
    getDailyTicker(market).then((response) => {
      if (isMountedRef.current) {
        setTeams(response.data || []);
      }
    }).catch((error) => {
      if (isMountedRef.current) {
        onError(error);
      }
    });
  }

  const handleMarketChanged = (param) => {
    setSelectedMarket(param);
  }

  const filterMarkets = () => {
    return Promise.resolve(MARKETS);
  }

  return (
    <>{showOverlay &&
      <div id='overlay-side-filters' onClick={() => {
        let closeCanvas = document.getElementById('closeTeamList');
        closeCanvas.click()
      }}></div>}
    <div
      className="offcanvas offcanvas-end"
      data-bs-scroll="true"
      data-bs-backdrop="false"
      tabIndex="-1"
      id="offcanvasTeamList"
      aria-labelledby="offcanvasTeamList"
      style={{ width: '425px', top: top ? top : '0' }}
    >
      <div className="offcanvas-header">
        <h5 className="offcanvas-title me-3" id="offcanvasTeamList">Team List</h5>
        <DropDownDiv>
          <DropDownList
            showSearchTextbox={false}
            showClearButton={false}
            placeholder="Filter Market"
            header=""
            handleChange={handleMarketChanged}
            height="5"
            getData={filterMarkets}
            selected={selectedMarkets}
          />
        </DropDownDiv>
        <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close" id="closeTeamList"></button>
      </div>
      <div className="offcanvas-body">
        <Row>
          <Col>
            {teams.map((team, i) =>
              <Accordion key={team.teamId ?? team.teamName ?? i} defaultActiveKey="">
                <Card className="rounded-3 mb-2">
                  <Card.Header className="d-flex align-items-center bg-white rounded-3 cursor-p border-0">
                    <PlusMinusToggle eventKey="0">
                      <div className="w-50 text-start overflow-hidden text-truncate nowrap me-3">
                        <span id={componentName + "-teamName-" + i}>{team.teamName}</span>
                      </div>
                      <div>
                        <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip">{team.teamName + "'s Daily ADT Capacity"}</Tooltip>}>
                          <Badge bg="primary" className="me-3" id={componentName + "-teamCapacity-" + i}>{(team.teamCapacitySum)}</Badge>
                        </OverlayTrigger>
                        <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip">{team.teamName + "'s Daily Hub Assigned ADTs"}</Tooltip>}>
                          <Badge bg="" text="dark" className="border me-2" id={componentName + "-teamDailyAssigned-" + i}>{(team.teamDailyAssignment || 0)}</Badge>
                        </OverlayTrigger>
                        <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip">{team.teamName + "'s Total Caseload"}</Tooltip>}>
                          <Badge bg="" text="dark" className="border" id={componentName + "-teamTotalAssigned-" + i}>{(team.teamHistoricalAssignment || 0)}</Badge>
                        </OverlayTrigger>
                      </div>
                    </PlusMinusToggle>
                  </Card.Header>
                  <Accordion.Collapse eventKey="0">
                    <Card.Body>
                      {team.programLeads?.map((m, index) =>
                        <div key={m.programLeadId ?? m.programLeadName ?? index} className="d-flex justify-content-between align-items-center mb-1">
                          <div className="w-50 overflow-hidden text-truncate nowrap me-3">
                            <span id={componentName + "-memberName-" + index}>{m.programLeadName}</span>
                            {m.zone.length > 0 &&
                              <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id={`tooltip-${index}-zone`} className="tooltip">{m.zone}</Tooltip>} placement="top-start">
                                <small className="fst-italic ms-2" id={componentName + "-programLeadZone-" + index}>({m.zone})</small>
                              </OverlayTrigger>
                            }
                          </div>
                          <div>
                            <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip">{m.programLeadName + "'s Daily ADT Capacity"}</Tooltip>}>
                              <Badge bg="primary" className="me-3" id={componentName + "-memberCapacity-" + index}>{(m.dailyCapacity).padStart(2, '0')}</Badge>
                            </OverlayTrigger>
                            <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip">{m.programLeadName + "'s Daily Hub Assigned ADTs"}</Tooltip>}>
                              <Badge bg="" text="dark" className="border me-2" id={componentName + "-memberDailyAssigned-" + index}>{m.dailyAssignment}</Badge>
                            </OverlayTrigger>
                            <OverlayTrigger trigger={['hover', 'focus']} overlay={<Tooltip id="tooltip">{m.programLeadName + "'s Total Caseload"}</Tooltip>}>
                              <Badge bg="" text="dark" className="border" id={componentName + "-memberTotalAssigned-" + index}>{(m.totalAssignment || 0)}</Badge>
                            </OverlayTrigger>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              </Accordion>
            )}
          </Col>
        </Row>
      </div>
    </div></>
  )
}

export default PatientListOffCanvas
